import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PiArrowsLeftRight, PiCaretRight, PiShieldCheck, PiSignOut, PiSortAscending, PiSwatches, PiTag, PiTrash, PiUsersThree } from 'react-icons/pi';
import AccountPageHeader from '../components/AccountPageHeader';
import PageIntro from '../components/PageIntro';
import { PoweredBy } from '../components/PoweredBy';
import { auth } from '../firebaseConfig';
import { getAccountDisplayName, getAccountTypeLabel } from '../utils/accountPresentation';
import '../styles/account-pages.css';
import AccountDeletionFlow from '../components/AccountDeletionFlow';

const Profile = ({ currentUser, currentRole, currentAccount, onOpenAccountSwitcher }) => {
  const navigate = useNavigate();
  const userType = currentRole === 'owner'
    ? 'Titular'
    : currentRole === 'admin'
      ? 'Administrador'
      : currentRole === 'editor'
      ? 'Editor'
      : currentRole === 'viewer'
        ? 'Visualizador'
        : 'Conta Nara';

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const settingsGroups = [
    {
      label: 'Aparência',
      items: [{ path: '/appearance', label: 'Aparência', description: 'Claro, escuro ou conforme o sistema', icon: <PiSwatches size={22} /> }],
    },
    {
      label: 'Preferências',
      items: [{ path: '/sort-settings', label: 'Ordenação', description: 'Como suas movimentações são exibidas', icon: <PiSortAscending size={22} /> }],
    },
    {
      label: 'Segurança',
      items: [{ path: '/security', label: 'Segurança', description: 'Proteja sua conta Nara', icon: <PiShieldCheck size={22} /> }],
    },
  ];

  const displayName = currentUser.displayName || 'Usuário';
  const initial = displayName.charAt(0).toUpperCase();
  const currentAccountName = getAccountDisplayName(currentAccount);
  const currentAccountType = getAccountTypeLabel(currentAccount, currentUser.uid);

  const [avatarError, setAvatarError] = useState(false);
  const [showDeletion, setShowDeletion] = useState(false);

  return (
    <div className="account-page">
      <AccountPageHeader title="Perfil" onBack={() => navigate(-1)} />
      <main className="account-content">
        <PageIntro description="Gerencie sua conta e as preferências da Nara." />
        <section className="profile-identity" aria-label="Identidade do usuário">
          <div className="profile-avatar">
            {currentUser.photoURL && !avatarError ? (
              <img
                src={currentUser.photoURL}
                alt=""
                className="profile-avatar__image"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <span
                className="profile-avatar__placeholder"
                aria-hidden="true"
              >
                {initial}
              </span>
            )}
          </div>
          <div>
            <h2>{displayName}</h2>
            <p>{userType || 'Conta Nara'}</p>
          </div>
        </section>

        <section className="settings-surface" aria-label="Preferências e conta">
          <div className="settings-groups">
            <section className="settings-group" aria-labelledby="settings-group-conta">
              <h3 id="settings-group-conta">Conta</h3>
              <div className="settings-list">
                <button type="button" className="settings-link settings-link--button" onClick={(event) => onOpenAccountSwitcher(event.currentTarget)} aria-label={`Trocar conta. Atual: ${currentAccountName}`}>
                  <span className="settings-link__icon" aria-hidden="true"><PiArrowsLeftRight size={22} /></span>
                  <span className="settings-link__content"><strong>{currentAccountName}</strong><small>{currentAccountType}</small></span>
                  <PiCaretRight className="settings-link__chevron" size={20} aria-hidden="true" />
                </button>
                <Link className="settings-link" to="/categories">
                  <span className="settings-link__icon" aria-hidden="true"><PiTag size={22} /></span>
                  <span className="settings-link__content"><strong>Categorias</strong><small>Personalize categorias desta conta</small></span>
                  <PiCaretRight className="settings-link__chevron" size={20} aria-hidden="true" />
                </Link>
                {currentAccount?.type === 'family' && (
                  <Link className="settings-link" to="/family-members">
                    <span className="settings-link__icon" aria-hidden="true"><PiUsersThree size={22} /></span>
                    <span className="settings-link__content"><strong>Família e membros</strong><small>Relações e permissões desta conta</small></span>
                    <PiCaretRight className="settings-link__chevron" size={20} aria-hidden="true" />
                  </Link>
                )}
              </div>
            </section>
            {settingsGroups.map((group) => (
              <section className="settings-group" aria-labelledby={`settings-group-${group.label}`} key={group.label}>
                <h3 id={`settings-group-${group.label}`}>{group.label}</h3>
                <nav className="settings-list" aria-label={group.label}>
                  {group.items.map((item) => (
                    <Link className="settings-link" to={item.path} key={item.path}>
                      <span className="settings-link__icon" aria-hidden="true">{item.icon}</span>
                      <span className="settings-link__content"><strong>{item.label}</strong><small>{item.description}</small></span>
                      <PiCaretRight className="settings-link__chevron" size={20} aria-hidden="true" />
                    </Link>
                  ))}
                </nav>
              </section>
            ))}
          </div>
          <div className="settings-surface__footer">
            <button type="button" className="account-logout account-logout--session" onClick={handleLogout}>
              <PiSignOut size={20} aria-hidden="true" />
              Sair da conta
            </button>
            <button type="button" className="account-logout account-logout--danger" onClick={() => setShowDeletion(true)}>
              <PiTrash size={20} aria-hidden="true" />
              Excluir conta permanentemente
            </button>
          </div>
        </section>
      </main>
      <PoweredBy />
      {showDeletion && <div className="deletion-modal" role="dialog" aria-modal="true" aria-label="Excluir conta permanentemente">
        <div className="deletion-modal__panel"><AccountDeletionFlow onCancel={() => setShowDeletion(false)} /></div>
      </div>}
    </div>
  );
};

export default Profile;
